import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { supabase } from '../../lib/supabase.js';
import {
  TIPO_EVENTO,
  COMUNIDAD_TIPOS_PUBLICACION,
  comunidadTipoPublicacionLabel,
  comunidadDbRowToAdminForm,
  adminUpdateComunidadPublicacion,
} from '../../lib/comunidadPublicaciones.js';
import { invokeComunidadAiScreen } from '../../lib/comunidadAiScreen.js';
import { verifyComunidadEntradaPublication } from '../../lib/comunidadEntradaVerify.js';
import { PAISES_COMUN } from '../data/spanishProvinces.js';
import { DCard, BtnGhost } from '../components/MarketplaceAtoms.js';
import { GROOVES } from '../constants/groovesEconomy.js';
import { notifyComunidadAutorModeracion } from '../../lib/comunidadModeracionNotify.js';
import {
  comunidadModeracionEstado,
  comunidadModeracionEstadoPillStyle,
} from '../../lib/comunidadModeracionEstado.js';
import { isComunidadEventoN8n, comunidadAutorLabel, comunidadFuenteDominio } from '../../lib/comunidadEventosN8n.js';
import ComunidadMusicGenresField from '../components/ComunidadMusicGenresField.jsx';
import { adminComunidadTabBadges } from '../../lib/adminMenuBadges.js';
import { ADMIN_SEG } from '../../lib/adminSeguimientoLecturas.js';
import {
  useAdminSeguimiento,
  AdminLeidaCell,
  AdminSeguimientoMarcarTodas,
  AdminSeguimientoTabLabel,
  adminSeguimientoRowStyle,
} from '../components/AdminSeguimientoLeido.js';
import { AdminAlertDot } from '../../lib/adminMenuBadges.js';
import AdminSocialKitModal from '../components/AdminSocialKitModal.jsx';
import AdminComunidadDuplicateCompareModal from '../components/AdminComunidadDuplicateCompareModal.jsx';
import AdminInstagramCarouselBar from '../components/AdminInstagramCarouselBar.jsx';
import { useAdminCarouselSelection } from '../hooks/useAdminCarouselSelection.js';
import { getComunidadEventDate, sortComunidadRowsByEventDate } from '../../lib/comunidadEventDate.js';
import { resolveAdminInvokeError } from '../../lib/adminActionsInvoke.js';
import { comunidadTelegramCarouselSentInfo } from '../../lib/comunidadTelegramCarousel.js';
import {
  comunidadDuplicateStatusColor,
  comunidadDuplicateStatusLabel,
  comunidadHasDuplicateAlert,
  getComunidadDuplicateCheck,
  refreshComunidadDuplicateCheck,
} from '../../lib/comunidadDuplicateCheck.js';
import {
  isComunidadImagenInOurStorage,
  uploadComunidadImagenAdmin,
} from '../../lib/comunidadImagenesService.js';

function comunidadCarouselImagenUrl(row) {
  const c = row?.cuerpo && typeof row.cuerpo === 'object' ? row.cuerpo : {};
  return String(c.imagenUrl || '').trim();
}

function isComunidadCarouselEligible(row) {
  return row?.publicacion_tipo === TIPO_EVENTO && comunidadCarouselImagenUrl(row).length > 0;
}

const btnMini = {
  padding: '5px 10px',
  borderRadius: 8,
  border: '0.5px solid rgba(200,168,75,0.35)',
  background: 'rgba(200,168,75,0.08)',
  color: C.gold,
  fontSize: 10,
  fontWeight: 600,
  cursor: 'pointer',
};

const ENTRADA_TIPO_OPCIONES = [
  { v: 'libre', l: 'Libre / Gratuito' },
  { v: 'consumicion', l: 'Con consumición' },
  { v: 'precio_fijo', l: 'Precio fijo' },
  { v: 'invitacion', l: 'Por invitación' },
  { v: 'donacion', l: 'Donación voluntaria' },
  { v: 'por_confirmar', l: 'Por confirmar' },
  { v: 'otros', l: 'Otros' },
];

const TIPO_SITIO_CORTO = {
  'Tienda de vinilos': 'Tienda',
  'Pub / bar': 'Pub',
  'Discoteca / sala': 'Sala',
  'Mercadillo u ocasión': 'Mercadillo',
  'Otro (lugar musical)': 'Lugar',
};

const inpModal = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px solid rgba(0,0,0,0.12)',
  background: '#fff',
  color: '#111',
  fontSize: 13,
  boxSizing: 'border-box',
};

const lblModal = { fontSize: 11, fontWeight: 700, color: '#444', marginBottom: 6, display: 'block' };

/** Motivos de rechazo moderación (código guardado en BD + texto para el autor). */
const COMUNIDAD_RECHAZO_MOTIVOS = [
  { codigo: 'contenido_inadecuado', etiqueta: 'Contenido inadecuado o fuera de tono' },
  { codigo: 'spam_publicidad', etiqueta: 'Spam o publicidad no permitida' },
  { codigo: 'datos_falsos_incompletos', etiqueta: 'Datos falsos o información insuficiente' },
  { codigo: 'imagen_calidad', etiqueta: 'Imagen ilegible, inadecuada o con derechos de terceros' },
  { codigo: 'duplicado', etiqueta: 'Contenido duplicado o repetido' },
  { codigo: 'normas_comunidad', etiqueta: 'Incumple las normas de Comunidad', requiereDetalle: true },
];

function etiquetaMotivoRechazo(codigo) {
  return COMUNIDAD_RECHAZO_MOTIVOS.find(m => m.codigo === codigo)?.etiqueta || codigo || '—';
}

function formatFecha(ts) {
  if (!ts) return '—';
  try {
    return new Intl.DateTimeFormat('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(ts));
  } catch {
    return '—';
  }
}

function formatFechaEventoRow(row) {
  const d = getComunidadEventDate({
    _cuerpo: row?.cuerpo && typeof row.cuerpo === 'object' ? row.cuerpo : {},
    fuenteFecha: row?.fuente_fecha,
    date: row?.cuerpo?.fecha,
  });
  if (!d) return '—';
  try {
    return new Intl.DateTimeFormat('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(d);
  } catch {
    return '—';
  }
}

/** Resumen de ubicación desde cuerpo jsonb */
function sitioBreve(row) {
  const c = row?.cuerpo && typeof row.cuerpo === 'object' ? row.cuerpo : {};
  const p = [c.ciudad, c.via].filter(Boolean).join(', ');
  return String(p || c.place || '').trim() || '—';
}

function aiSuggestedActionLabel(action) {
  if (action === 'approve') return 'Aprobar';
  if (action === 'reject') return 'Rechazar';
  return 'Revisar';
}

function aiSuggestedActionColor(action) {
  if (action === 'approve') return C.green;
  if (action === 'reject') return C.danger;
  return C.gold;
}

function aiCheckStatusMeta(status) {
  if (status === 'ok') return { icon: '✓', color: C.green };
  if (status === 'fail') return { icon: '✕', color: C.danger };
  return { icon: '!', color: C.warning };
}

/** Checklist visual: usa checks[] de n8n o convierte reasons en filas compactas. */
function resolveAiChecks(screening) {
  if (!screening || typeof screening !== 'object') return [];
  if (Array.isArray(screening.checks) && screening.checks.length > 0) {
    return screening.checks.map((check, i) => ({
      id: check.id || `check_${i}`,
      label: String(check.label || '').trim(),
      status: check.status === 'ok' || check.status === 'fail' ? check.status : 'warn',
      detail: String(check.detail || '').trim(),
    })).filter(c => c.label);
  }
  if (Array.isArray(screening.reasons) && screening.reasons.length > 0) {
    const failish = screening.suggested_action === 'reject';
    return screening.reasons.map((reason, i) => ({
      id: `reason_${i}`,
      label: String(reason).trim(),
      status: failish ? 'fail' : 'warn',
      detail: '',
    })).filter(c => c.label);
  }
  const summary = String(screening.verdict_line || screening.summary || '').trim();
  if (summary) {
    return [{ id: 'summary', label: summary, status: 'warn', detail: '' }];
  }
  return [];
}

function entradaEtiqueta(v) {
  return ENTRADA_TIPO_OPCIONES.find(o => o.v === v)?.l || v || '—';
}

function tipoSitioCorto(tipo) {
  return TIPO_SITIO_CORTO[tipo] || 'Sitio';
}

/**
 * Moderación del feed Comunidad (similar a Noticias: pendientes ↔ publicadas en feed).
 */
const COMUNIDAD_ADMIN_TABS = new Set(['pendientes', 'eventos_n8n', 'feed', 'ocultas']);

export default function AdminComunidadPanel({
  onError,
  onSuccess,
  user,
  onBadgesRefresh,
  menuBadges,
  initialTab = null,
}) {
  const [tab, setTab] = useState(() =>
    COMUNIDAD_ADMIN_TABS.has(initialTab) ? initialTab : 'pendientes'
  );
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [namesById, setNamesById] = useState({});
  const [aiRow, setAiRow] = useState(null);
  const [aiScreening, setAiScreening] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [entradaRow, setEntradaRow] = useState(null);
  const [entradaScreening, setEntradaScreening] = useState(null);
  const [entradaLoading, setEntradaLoading] = useState(false);
  const [entradaError, setEntradaError] = useState('');
  const [previewRow, setPreviewRow] = useState(null);
  const [previewEditMode, setPreviewEditMode] = useState(false);
  const [previewDraft, setPreviewDraft] = useState(null);
  const [previewSaving, setPreviewSaving] = useState(false);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectCodigo, setRejectCodigo] = useState(COMUNIDAD_RECHAZO_MOTIVOS[0].codigo);
  const [rejectDetalle, setRejectDetalle] = useState('');
  const [socialKitOpen, setSocialKitOpen] = useState(null);
  const [dupModalRow, setDupModalRow] = useState(null);
  const loadReqId = useRef(0);
  const dupRefreshReq = useRef(0);
  const cartelFileInputRef = useRef(null);
  const cartelUploadTargetRef = useRef(null);
  const [uploadingCartelId, setUploadingCartelId] = useState(null);
  const [vaciandoBandeja, setVaciandoBandeja] = useState(false);
  const [fechaEventoSort, setFechaEventoSort] = useState('asc');

  const applyBandejaInboxFilter = useCallback(
    q => {
      if (tab === 'pendientes') {
        return q.eq('aprobada', false).eq('is_hidden', false).or('metadata.is.null,metadata->>source.neq.n8n');
      }
      if (tab === 'eventos_n8n') {
        return q.eq('aprobada', false).eq('is_hidden', false).eq('metadata->>source', 'n8n');
      }
      return q;
    },
    [tab],
  );

  const tabs = useMemo(
    () => [
      { id: 'pendientes', label: 'Pendientes (usuarios)' },
      { id: 'eventos_n8n', label: 'Borradores n8n' },
      { id: 'feed', label: 'En el feed público' },
      { id: 'ocultas', label: 'Ocultas / rechazadas' },
    ],
    []
  );

  const tabBadges = useMemo(() => adminComunidadTabBadges(menuBadges), [menuBadges]);

  useEffect(() => {
    if (COMUNIDAD_ADMIN_TABS.has(initialTab)) setTab(initialTab);
  }, [initialTab]);

  const tabBadgeCount = useCallback(
    tabId => {
      if (tabId === 'pendientes') return tabBadges.pendientes;
      if (tabId === 'eventos_n8n') return tabBadges.eventos_n8n;
      if (tabId === 'feed') return tabBadges.portada;
      return 0;
    },
    [tabBadges]
  );

  const seguimientoSection =
    tab === 'pendientes'
      ? ADMIN_SEG.COMUNIDAD_PENDIENTE
      : tab === 'eventos_n8n'
        ? ADMIN_SEG.COMUNIDAD_EVENTOS_N8N
        : tab === 'feed'
          ? ADMIN_SEG.COMUNIDAD_PORTADA
          : null;
  const seguimientoRows = useMemo(() => {
    if (tab === 'pendientes' || tab === 'eventos_n8n') return rows;
    if (tab === 'feed') return rows.filter(r => r.destacada_solicitud_pendiente && !r.destacada);
    return [];
  }, [tab, rows]);
  const showSeguimiento = tab === 'pendientes' || tab === 'eventos_n8n' || tab === 'feed';
  const carouselFeed = tab === 'feed';
  const showCarouselSentCol = tab === 'feed';
  const showFechaEventoCol = tab === 'eventos_n8n' || tab === 'feed';
  const showDupCol = tab === 'pendientes' || tab === 'eventos_n8n';
  const showEstadoCol = tab === 'pendientes' || tab === 'eventos_n8n' || tab === 'feed' || tab === 'ocultas';
  const displayRows = useMemo(() => {
    if (!showFechaEventoCol) return rows;
    return sortComunidadRowsByEventDate(rows, fechaEventoSort);
  }, [rows, showFechaEventoCol, fechaEventoSort]);
  const carouselSel = useAdminCarouselSelection({ enabled: carouselFeed, max: 10 });
  const carouselEligibleRows = useMemo(
    () => (carouselFeed ? rows.filter(isComunidadCarouselEligible) : []),
    [carouselFeed, rows],
  );

  useEffect(() => {
    carouselSel.clear();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab === 'eventos_n8n' || tab === 'feed') setFechaEventoSort('asc');
  }, [tab]);

  const seguimiento = useAdminSeguimiento({
    adminId: user?.id,
    section: seguimientoSection || ADMIN_SEG.COMUNIDAD_PENDIENTE,
    rows: showSeguimiento ? seguimientoRows : [],
    onBadgesRefresh,
    onError,
  });

  const attachUsernames = useCallback(async list => {
    const ids = [...new Set((list || []).map(r => r.author_id).filter(Boolean))];
    if (!ids.length) {
      setNamesById({});
      return;
    }
    const { data: profs } = await supabase.from('profiles').select('id, username').in('id', ids);
    setNamesById(Object.fromEntries((profs || []).map(p => [p.id, p.username || null])));
  }, []);

  const load = useCallback(async () => {
    const reqId = ++loadReqId.current;
    setLoading(true);
    onError('');
    try {
      let q = supabase
        .from('comunidad_publicaciones')
        .select(
          'id, author_id, title, publicacion_tipo, cuerpo, created_at, entrada_tipo, entrada_precio, entrada_enlace, entradas_verificadas, aprobada, is_hidden, hidden_reason, moderacion_rechazo_codigo, moderacion_rechazo_detalle, destacada, destacada_solicitud_pendiente, destacada_solicitud_at, destacada_grooves_cobrados, metadata, external_key, fuente_url, fuente_fecha'
        )
        .order('created_at', { ascending: false })
        .limit(200);

      if (tab === 'pendientes' || tab === 'eventos_n8n') {
        q = applyBandejaInboxFilter(q);
      } else if (tab === 'feed') {
        q = q.eq('aprobada', true).eq('is_hidden', false);
      } else {
        q = q.eq('is_hidden', true);
      }

      const { data, error } = await q;
      if (reqId !== loadReqId.current) return;
      if (error) {
        onError(error.message || 'No se pudieron cargar las publicaciones');
        setRows([]);
        return;
      }
      const list = data || [];
      setRows(list);
      await attachUsernames(list);

      if (tab === 'pendientes' || tab === 'eventos_n8n') {
        const rq = ++dupRefreshReq.current;
        const needCheck = list
          .filter(
            r =>
              r.publicacion_tipo === TIPO_EVENTO &&
              !getComunidadDuplicateCheck(r)?.checked_at,
          )
          .slice(0, 6);
        for (const row of needCheck) {
          if (rq !== dupRefreshReq.current) break;
          try {
            const r = await refreshComunidadDuplicateCheck(supabase, row.id);
            if (r.ok && rq === dupRefreshReq.current) {
              setRows(prev =>
                prev.map(p => {
                  if (p.id !== row.id) return p;
                  const meta = p.metadata && typeof p.metadata === 'object' ? { ...p.metadata } : {};
                  return { ...p, metadata: { ...meta, duplicate_check: r.duplicate_check } };
                }),
              );
            }
          } catch {
            /* ignore background dup check */
          }
        }
      }
    } catch (e) {
      if (reqId !== loadReqId.current) return;
      onError(e?.message || 'Error al cargar');
      setRows([]);
    } finally {
      if (reqId === loadReqId.current) setLoading(false);
    }
  }, [onError, tab, attachUsernames, applyBandejaInboxFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const descartarSolicitudDestacado = async row => {
    if (!row.destacada_solicitud_pendiente) return;
    if (!window.confirm('¿Descartar la solicitud de portada sin marcar destacada ni cobrar grooves?')) return;
    setBusyId(row.id);
    onError('');
    try {
      const { error } = await supabase
        .from('comunidad_publicaciones')
        .update({ destacada_solicitud_pendiente: false })
        .eq('id', row.id);
      if (error) throw error;
      await load();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo descartar la solicitud');
    } finally {
      setBusyId(null);
    }
  };

  const toggleDestacada = async row => {
    const next = !row.destacada;
    const cost = GROOVES.COMUNIDAD_DESTACAR_PARTNER;
    setBusyId(row.id);
    onError('');
    try {
      if (!next) {
        const { error } = await supabase.from('comunidad_publicaciones').update({ destacada: false }).eq('id', row.id);
        if (error) throw error;
        await load();
        onBadgesRefresh?.();
        return;
      }

      const patchBase = {
        destacada: true,
        destacada_solicitud_pendiente: false,
        destacada_grooves_cobrados: null,
      };

      if (row.destacada_solicitud_pendiente) {
        if (
          !window.confirm(
            `Hay solicitud de portada: primero se cobrarán ${cost} G en cuenta partner si hay saldo; si no, del saldo DreamVinyl general del autor (${cost} G). ¿Continuar?`
          )
        ) {
          return;
        }
        const { data: cobroData, error: cobroErr } = await supabase.rpc(
          'comunidad_admin_aplicar_destacada_portada',
          {
            p_publicacion_id: row.id,
            p_sin_cobro: false,
          }
        );
        if (cobroErr) throw cobroErr;
        const res = cobroData && typeof cobroData === 'object' ? cobroData : {};
        if (!res.ok) {
          const code = res.code;
          const pg = res.grooves_partner;
          const pr = res.grooves_profile;
          if (code === 'insufficient_balance') {
            const det =
              pg != null && pg !== undefined
                ? `Partner: ${pg} G · Perfil: ${pr ?? 0} G (necesarios ${cost} G).`
                : `Saldo general del autor: ${pr ?? 0} G (necesarios ${cost} G).`;
            if (
              window.confirm(
                `${det}\n\n¿Marcar en portada igualmente sin cobrar grooves? (solo uso admin excepcional)`
              )
            ) {
              const { data: freeData, error: freeErr } = await supabase.rpc(
                'comunidad_admin_aplicar_destacada_portada',
                {
                  p_publicacion_id: row.id,
                  p_sin_cobro: true,
                }
              );
              if (freeErr) throw freeErr;
              const freeRes = freeData && typeof freeData === 'object' ? freeData : {};
              if (!freeRes.ok) {
                onError(freeRes.message || freeRes.code || 'No se pudo aplicar destacada sin cobro.');
                return;
              }
            } else {
              return;
            }
          } else if (code === 'no_pending_request') {
            const { error } = await supabase.from('comunidad_publicaciones').update(patchBase).eq('id', row.id);
            if (error) throw error;
          } else {
            onError(res.message || String(code || 'No se pudo aplicar el cobro de portada'));
            return;
          }
        }
      } else {
        const { error } = await supabase.from('comunidad_publicaciones').update(patchBase).eq('id', row.id);
        if (error) throw error;
      }

      await load();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo actualizar destacada');
    } finally {
      setBusyId(null);
    }
  };

  const aprobarAlFeed = async row => {
    setBusyId(row.id);
    onError('');
    try {
      const { data, error } = await supabase.functions.invoke('admin-actions', {
        body: {
          action: 'comunidad_approve_publication',
          payload: { publicacion_id: row.id, titulo: row?.title ?? null },
        },
      });
      if (error || !data?.ok) {
        throw new Error(await resolveAdminInvokeError(error, data, 'No se pudo aprobar la publicación.'));
      }
      await load();
      onBadgesRefresh?.();
      if (data?.imagen_persisted) {
        onSuccess?.(
          data?.imagen_compressed
            ? 'Imagen guardada y optimizada en el servidor. Publicación aprobada en el feed.'
            : 'Imagen guardada en el servidor. Publicación aprobada en el feed.',
        );
      } else if (data?.telegram_auto?.ok) {
        onSuccess?.('Publicación aprobada en el feed y enviada a Telegram automáticamente.');
      } else if (data?.telegram_auto && !data.telegram_auto.ok) {
        onSuccess?.('Publicación aprobada en el feed.');
        onError?.(
          data.telegram_auto.queued
            ? `${data.telegram_auto.message || 'Telegram falló.'} Reintenta en Admin → Cola redes → Procesar cola.`
            : data.telegram_auto.message ||
                'Telegram automático falló. Usa Redes → Telegram o revisa n8n.',
        );
      } else {
        onSuccess?.('Publicación aprobada en el feed.');
      }
      if (row.author_id && !isComunidadEventoN8n(row)) {
        const { error: nErr } = await notifyComunidadAutorModeracion(supabase, {
          recipientId: row.author_id,
          publicationId: row.id,
          publicationTitle: row.title,
          approved: true,
        });
        if (nErr) onError(`Publicación aprobada. No se pudo enviar el aviso al autor: ${nErr.message}`);
      }
    } catch (e) {
      const msg = e?.message || 'No se pudo aprobar';
      const hint403 =
        /403|bloquea la descarga/i.test(msg) && isComunidadEventoN8n(row)
          ? ' Usa «Subir cartel» en la fila o en la vista previa y vuelve a aprobar.'
          : '';
      onError(`${msg}${hint403}`);
    } finally {
      setBusyId(null);
    }
  };

  const abrirRechazo = row => {
    setRejectModal(row);
    setRejectCodigo(COMUNIDAD_RECHAZO_MOTIVOS[0].codigo);
    setRejectDetalle('');
    onError('');
  };

  const cerrarRechazoModal = () => {
    setRejectModal(null);
    setRejectDetalle('');
  };

  const confirmarRechazo = async () => {
    if (!rejectModal) return;
    const row = rejectModal;
    const motivo = COMUNIDAD_RECHAZO_MOTIVOS.find(m => m.codigo === rejectCodigo);
    const label = motivo?.etiqueta || rejectCodigo;
    const det = rejectDetalle.trim();
    if (motivo?.requiereDetalle && !det) {
      onError('Para «Incumple las normas de Comunidad» indica un detalle concreto.');
      return;
    }
    const hiddenReason = `[${rejectCodigo}] ${label}` + (det ? ` — ${det}` : '');
    setBusyId(row.id);
    onError('');
    try {
      const { error } = await supabase
        .from('comunidad_publicaciones')
        .update({
          is_hidden: true,
          hidden_reason: hiddenReason,
          hidden_at: new Date().toISOString(),
          hidden_by: user?.id || null,
          moderacion_rechazo_codigo: rejectCodigo,
          moderacion_rechazo_detalle: det || null,
        })
        .eq('id', row.id);
      if (error) throw error;
      cerrarRechazoModal();
      await load();
      onBadgesRefresh?.();
      if (row.author_id && !isComunidadEventoN8n(row)) {
        const { error: nErr } = await notifyComunidadAutorModeracion(supabase, {
          recipientId: row.author_id,
          publicationId: row.id,
          publicationTitle: row.title,
          approved: false,
          rejectCodigo,
          rejectLabel: label,
          rejectDetalle: det,
        });
        if (nErr) onError(`Rechazo aplicado. No se pudo enviar el aviso al autor: ${nErr.message}`);
      }
    } catch (e) {
      onError(e?.message || 'No se pudo rechazar');
    } finally {
      setBusyId(null);
    }
  };

  const quitarOcultacion = async row => {
    if (!window.confirm('¿Mostrar de nuevo esta publicación en el lugar que corresponda (pendiente si no estaba aprobada)?'))
      return;
    setBusyId(row.id);
    onError('');
    try {
      const { error } = await supabase
        .from('comunidad_publicaciones')
        .update({
          is_hidden: false,
          hidden_reason: null,
          hidden_at: null,
          hidden_by: null,
        })
        .eq('id', row.id);
      if (error) throw error;
      await load();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo restaurar');
    } finally {
      setBusyId(null);
    }
  };

  /** Quitar del feed público sin borrar la fila (sigue editable por admin/ocultas) */
  const retirarDelFeed = async row => {
    if (!window.confirm('¿Ocultar esta publicación para el público (sigue visible en Ocultas para revisión)?'))
      return;
    setBusyId(row.id);
    onError('');
    try {
      const { error } = await supabase
        .from('comunidad_publicaciones')
        .update({
          is_hidden: true,
          hidden_reason: 'Retirada del feed por administración',
          hidden_at: new Date().toISOString(),
          hidden_by: user?.id || null,
        })
        .eq('id', row.id);
      if (error) throw error;
      await load();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo retirar');
    } finally {
      setBusyId(null);
    }
  };

  const eliminar = async row => {
    const msgN8n =
      '¿Eliminar este borrador n8n? Se borrará por completo (no va a Ocultas ni envía avisos a ningún usuario).';
    const msgFeed =
      '¿Eliminar permanentemente esta publicación y sus guardados asociados?';
    const msg = isComunidadEventoN8n(row) ? msgN8n : msgFeed;
    if (!window.confirm(msg)) return;
    setBusyId(row.id);
    onError('');
    try {
      const { error } = await supabase.from('comunidad_publicaciones').delete().eq('id', row.id);
      if (error) throw error;
      await load();
      onBadgesRefresh?.();
    } catch (e) {
      onError(e?.message || 'No se pudo eliminar');
    } finally {
      setBusyId(null);
    }
  };

  const vaciarBandeja = async () => {
    if (tab !== 'pendientes' && tab !== 'eventos_n8n') return;
    onError('');
    try {
      const { count, error: countErr } = await applyBandejaInboxFilter(
        supabase.from('comunidad_publicaciones').select('id', { count: 'exact', head: true }),
      );
      if (countErr) throw countErr;
      const n = count ?? 0;
      if (!n) {
        onError('La bandeja ya está vacía.');
        return;
      }
      const msg =
        tab === 'eventos_n8n'
          ? `¿Eliminar los ${n} borrador(es) n8n de esta bandeja?\n\nBorrado permanente. No van a Ocultas ni se envía ningún aviso.`
          : `¿Eliminar las ${n} publicación(es) pendientes de esta bandeja?\n\nBorrado permanente. No van a Ocultas ni se notifica a los autores (usa «Rechazar» si quieres avisarles).`;
      if (!window.confirm(msg)) return;

      setVaciandoBandeja(true);
      const { error: delErr } = await applyBandejaInboxFilter(
        supabase.from('comunidad_publicaciones').delete(),
      );
      if (delErr) throw delErr;

      setPreviewRow(null);
      setPreviewDraft(null);
      setPreviewEditMode(false);
      setRejectModal(null);
      setDupModalRow(null);
      cerrarAiModal();
      cerrarEntradaModal();

      await load();
      onBadgesRefresh?.();
      onSuccess?.(
        tab === 'eventos_n8n'
          ? `Bandeja vaciada: ${n} borrador(es) n8n eliminado(s).`
          : `Bandeja vaciada: ${n} publicación(es) pendiente(s) eliminada(s).`,
      );
    } catch (e) {
      onError(e?.message || 'No se pudo vaciar la bandeja');
    } finally {
      setVaciandoBandeja(false);
    }
  };

  const cerrarAiModal = () => {
    setAiRow(null);
    setAiScreening(null);
    setAiError('');
    setAiLoading(false);
  };

  const patchRowDuplicateCheck = (id, duplicate_check) => {
    setRows(prev =>
      prev.map(r => {
        if (r.id !== id) return r;
        const meta = r.metadata && typeof r.metadata === 'object' ? { ...r.metadata } : {};
        return { ...r, metadata: { ...meta, duplicate_check } };
      }),
    );
    setDupModalRow(prev => {
      if (!prev || prev.id !== id) return prev;
      const meta = prev.metadata && typeof prev.metadata === 'object' ? { ...prev.metadata } : {};
      return { ...prev, metadata: { ...meta, duplicate_check } };
    });
  };

  const abrirCompararDuplicado = row => {
    setDupModalRow(row);
  };

  const analizarConIa = async row => {
    setAiRow(row);
    setAiScreening(null);
    setAiError('');
    setAiLoading(true);
    try {
      const r = await invokeComunidadAiScreen(supabase, row, {
        reviewContext: tab === 'eventos_n8n' ? 'n8n_borrador' : 'usuario_pendiente',
      });
      if (!r.ok) {
        setAiError(r.message || r.error || 'No se pudo obtener el análisis.');
        return;
      }
      setAiScreening(r.screening || null);
    } catch (e) {
      setAiError(e?.message || 'Error de red');
    } finally {
      setAiLoading(false);
    }
  };

  const eventoConEnlaceEntradas = row =>
    row?.publicacion_tipo === TIPO_EVENTO && String(row?.entrada_enlace ?? '').trim().length > 0;

  const cerrarEntradaModal = () => {
    setEntradaRow(null);
    setEntradaScreening(null);
    setEntradaError('');
    setEntradaLoading(false);
  };

  const verificarEnlaceEntradas = async (row, { force = true } = {}) => {
    setEntradaRow(row);
    setEntradaScreening(null);
    setEntradaError('');
    setEntradaLoading(true);
    try {
      const r = await verifyComunidadEntradaPublication(row.id, { force });
      if (!r.ok) {
        setEntradaError(r.error || 'No se pudo verificar el enlace.');
        return;
      }
      setEntradaScreening(r.screening || null);
      if (r.verified) await load();
    } catch (e) {
      setEntradaError(e?.message || 'Error de red');
    } finally {
      setEntradaLoading(false);
    }
  };

  const aprobarEntradaManual = async row => {
    if (!window.confirm('¿Marcar el enlace de entradas como verificado manualmente?')) return;
    setBusyId(row.id);
    onError('');
    try {
      const { error } = await supabase
        .from('comunidad_publicaciones')
        .update({ entradas_verificadas: true })
        .eq('id', row.id);
      if (error) throw error;
      await load();
    } catch (e) {
      onError(e?.message || 'No se pudo aprobar el enlace.');
    } finally {
      setBusyId(null);
    }
  };

  const cerrarVistaPrevia = () => {
    setPreviewRow(null);
    setPreviewEditMode(false);
    setPreviewDraft(null);
  };

  const abrirVistaPrevia = (row, { editar = false } = {}) => {
    setPreviewRow(row);
    setPreviewEditMode(editar);
    setPreviewDraft(comunidadDbRowToAdminForm(row));
  };

  const refrescarFilaPreview = async publicationId => {
    const { data: refreshed, error: e2 } = await supabase
      .from('comunidad_publicaciones')
      .select(
        'id, author_id, title, publicacion_tipo, cuerpo, created_at, entrada_tipo, entrada_precio, entrada_enlace, entradas_verificadas, aprobada, is_hidden, hidden_reason, moderacion_rechazo_codigo, moderacion_rechazo_detalle, destacada, destacada_solicitud_pendiente, destacada_solicitud_at, destacada_grooves_cobrados, metadata, external_key, fuente_url, fuente_fecha'
      )
      .eq('id', publicationId)
      .maybeSingle();
    if (e2) throw e2;
    if (refreshed && previewRow?.id === publicationId) {
      setPreviewRow(refreshed);
      setPreviewDraft(comunidadDbRowToAdminForm(refreshed));
    }
    return refreshed;
  };

  const guardarCartelEnPublicacion = async (row, publicUrl) => {
    const form = comunidadDbRowToAdminForm(row);
    form.imagenPreview = publicUrl;
    const { error } = await adminUpdateComunidadPublicacion(row.id, form);
    if (error) throw error;
    await refrescarFilaPreview(row.id);
    await load();
  };

  const subirCartelClick = row => {
    if (!row?.id) return;
    cartelUploadTargetRef.current = row;
    cartelFileInputRef.current?.click();
  };

  const onCartelArchivoSeleccionado = async e => {
    const row = cartelUploadTargetRef.current;
    cartelUploadTargetRef.current = null;
    const input = e.target;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !row?.id) return;
    if (!file.type?.startsWith('image/')) {
      onError('Formato no permitido. Usa JPG, PNG, GIF o WebP.');
      return;
    }

    setUploadingCartelId(row.id);
    onError('');
    try {
      const { url, error: upErr } = await uploadComunidadImagenAdmin(file, row.id, {
        authorId: row.author_id || null,
      });
      if (upErr || !url) throw new Error(upErr || 'No se pudo subir el cartel.');
      await guardarCartelEnPublicacion(row, url);
      onSuccess?.('Cartel guardado en el servidor. Ya puedes aprobar al feed.');
    } catch (err) {
      onError(err?.message || 'No se pudo subir el cartel.');
    } finally {
      setUploadingCartelId(null);
    }
  };

  const guardarEdicionVistaPrevia = async () => {
    if (!previewRow || !previewDraft) return;
    const tit = String(previewDraft.titulo || '').trim();
    if (!tit) {
      onError('El título no puede estar vacío.');
      return;
    }
    setPreviewSaving(true);
    onError('');
    try {
      const { error } = await adminUpdateComunidadPublicacion(previewRow.id, previewDraft);
      if (error) throw error;
      setPreviewEditMode(false);
      const { data: refreshed, error: e2 } = await supabase
        .from('comunidad_publicaciones')
        .select(
          'id, author_id, title, publicacion_tipo, cuerpo, created_at, entrada_tipo, entrada_precio, entrada_enlace, entradas_verificadas, aprobada, is_hidden, hidden_reason, moderacion_rechazo_codigo, moderacion_rechazo_detalle, destacada, destacada_solicitud_pendiente, destacada_solicitud_at, destacada_grooves_cobrados, metadata, external_key, fuente_url, fuente_fecha'
        )
        .eq('id', previewRow.id)
        .maybeSingle();
      if (e2) throw e2;
      if (refreshed) {
        setPreviewRow(refreshed);
        setPreviewDraft(comunidadDbRowToAdminForm(refreshed));
      }
      await load();
    } catch (e) {
      onError(e?.message || 'No se pudo guardar');
    } finally {
      setPreviewSaving(false);
    }
  };

  const riskColor = risk => {
    if (risk === 'high') return C.danger;
    if (risk === 'medium') return 'rgba(255,200,120,0.95)';
    return C.green;
  };

  return (
    <div>
      <input
        ref={cartelFileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
        style={{ display: 'none' }}
        onChange={onCartelArchivoSeleccionado}
      />
      <DCard style={{ padding: '14px 16px', marginBottom: 14 }}>
        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.52)', margin: '0 0 8px', lineHeight: 1.5 }}>
          <strong style={{ color: C.gold }}>Pendientes (usuarios):</strong> propuestas de la comunidad con flujo de moderación completo. Al{' '}
          <strong style={{ color: C.gold }}>aprobar</strong> o <strong style={{ color: C.gold }}>rechazar</strong>, el autor recibe una{' '}
          <strong style={{ color: 'rgba(255,255,255,0.75)' }}>notificación in-app</strong> (recibido, aprobado, rechazado, etc.). Los rechazos van a{' '}
          <strong style={{ color: C.gold }}>Ocultas</strong> con motivo.
        </p>
        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.52)', margin: '0 0 12px', lineHeight: 1.5 }}>
          <strong style={{ color: C.gold }}>Borradores n8n:</strong> eventos que envía el workflow automático (como los borradores de Noticias). Aquí los
          revisas, editas si hace falta, <strong style={{ color: C.gold }}>apruebas al feed</strong> o <strong style={{ color: C.gold }}>eliminas</strong>{' '}
          (borrado permanente, sin notificaciones ni cola de Ocultas). Solo tras aprobar aparecen en el feed público.
          La columna <strong style={{ color: C.gold }}>Dup.</strong> comprueba automáticamente si el evento ya está en el feed u otro borrador (capa 1).
          Pulsa el badge o <strong style={{ color: C.gold }}>Comparar dup.</strong> para ver las dos vistas previa lado a lado.
          <strong style={{ color: C.gold }}>Análisis IA</strong> (opcional) en Pendientes y Borradores n8n llama al mismo workflow n8n (
          <code style={{ fontSize: 10 }}>N8N_COMUNIDAD_USER_REVIEW_URL</code>). La IA devuelve resumen y sugerencia; tú decides aprobar o no.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          {tabs.map(t => {
            const badgeN = tabBadgeCount(t.id);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: tab === t.id ? `0.5px solid ${C.gold}` : '0.5px solid rgba(255,255,255,0.12)',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: tab === t.id ? 700 : 500,
                  color: tab === t.id ? C.gold : 'rgba(255,255,255,0.55)',
                  background: tab === t.id ? 'rgba(200,168,75,0.1)' : 'rgba(255,255,255,0.04)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <AdminSeguimientoTabLabel
                  label={t.label}
                  unreadCount={badgeN}
                  title={
                    t.id === 'feed'
                      ? `${badgeN} solicitud${badgeN === 1 ? '' : 'es'} de portada en el feed`
                      : t.id === 'eventos_n8n'
                        ? `${badgeN} borrador${badgeN === 1 ? '' : 'es'} n8n sin revisar`
                        : `${badgeN} publicación${badgeN === 1 ? '' : 'es'} de usuario pendiente${badgeN === 1 ? '' : 's'} de aprobación`
                  }
                />
              </button>
            );
          })}
          {showSeguimiento ? (
            <AdminSeguimientoMarcarTodas
              unreadCount={seguimiento.unreadCount}
              markingAllRead={seguimiento.markingAllRead}
              onMarcarTodas={seguimiento.marcarTodasLeidas}
            />
          ) : null}
          {tab === 'pendientes' || tab === 'eventos_n8n' ? (
            <button
              type="button"
              disabled={loading || vaciandoBandeja || !!busyId || rows.length === 0}
              title={
                tab === 'eventos_n8n'
                  ? 'Elimina todos los borradores n8n restantes de esta bandeja'
                  : 'Elimina todas las publicaciones pendientes restantes (sin notificar a autores)'
              }
              onClick={vaciarBandeja}
              style={{
                padding: '8px 12px',
                borderRadius: 8,
                border: '0.5px solid rgba(226,75,74,0.45)',
                background: 'rgba(226,75,74,0.1)',
                color: C.danger,
                fontSize: 11,
                fontWeight: 600,
                cursor: loading || vaciandoBandeja || busyId || rows.length === 0 ? 'not-allowed' : 'pointer',
                opacity: loading || vaciandoBandeja || busyId || rows.length === 0 ? 0.5 : 1,
              }}
            >
              {vaciandoBandeja ? 'Vaciando…' : `Vaciar bandeja${rows.length ? ` (${rows.length})` : ''}`}
            </button>
          ) : null}
          <BtnGhost type="button" style={{ fontSize: 11, width: 'auto', marginLeft: 'auto' }} onClick={() => load()}>
            Recargar
          </BtnGhost>
        </div>
      </DCard>

      {carouselFeed ? (
        <>
          <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.42)', margin: '0 0 12px', lineHeight: 1.45 }}>
            En el feed: marca eventos con imagen y «Enviar carrusel a Telegram». La columna <strong>Carrusel TG</strong>{' '}
            indica si ya se enviaron en un carrusel anterior (desde este despliegue).
          </p>
          <AdminInstagramCarouselBar
            tipo="eventos"
            selection={carouselSel}
            onError={onError}
            onSuccess={msg => {
              onSuccess?.(msg);
              load();
            }}
          />
        </>
      ) : null}

      <DCard style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 20, fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>Cargando…</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 1020 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'rgba(255,255,255,0.45)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  {carouselFeed ? (
                    <th style={{ padding: '10px 10px', width: 40 }} title="Selección carrusel Telegram (eventos con imagen)">
                      <input
                        type="checkbox"
                        aria-label="Seleccionar todos los eventos con imagen"
                        checked={
                          carouselEligibleRows.length > 0 &&
                          carouselEligibleRows.every(r => carouselSel.isSelected(r.id))
                        }
                        onChange={() => carouselSel.toggleAll(carouselEligibleRows.map(r => r.id))}
                      />
                    </th>
                  ) : null}
                  {showSeguimiento ? (
                    <th style={{ padding: '10px 10px', width: 72 }} title="Seguimiento de revisión">
                      Leída
                    </th>
                  ) : null}
                  <th style={{ padding: '10px 10px' }}>Título</th>
                  {showEstadoCol ? (
                    <th style={{ padding: '10px 10px', whiteSpace: 'nowrap' }} title="Aprobación y visibilidad en el feed">
                      Estado
                    </th>
                  ) : null}
                  <th style={{ padding: '10px 10px' }}>Tipo</th>
                  <th style={{ padding: '10px 10px' }}>Ubicación</th>
                  <th style={{ padding: '10px 10px' }}>Autor</th>
                  {showDupCol ? (
                    <th style={{ padding: '10px 10px', whiteSpace: 'nowrap' }} title="Comprobación automática de duplicados">
                      Dup.
                    </th>
                  ) : null}
                  {showFechaEventoCol ? (
                    <th
                      style={{
                        padding: '10px 10px',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        userSelect: 'none',
                        color: C.gold,
                      }}
                      title="Clic para invertir orden (sin fecha al final)"
                      onClick={() => setFechaEventoSort(s => (s === 'asc' ? 'desc' : 'asc'))}
                    >
                      Fecha evento {fechaEventoSort === 'asc' ? '↑' : '↓'}
                    </th>
                  ) : null}
                  <th style={{ padding: '10px 10px', whiteSpace: 'nowrap' }}>
                    {showFechaEventoCol ? 'Alta' : 'Fecha'}
                  </th>
                  {tab === 'feed' ? (
                    <>
                      <th style={{ padding: '10px 10px', whiteSpace: 'nowrap' }} title="Carrusel Telegram enviado desde admin">
                        Carrusel TG
                      </th>
                      <th style={{ padding: '10px 10px', whiteSpace: 'nowrap' }}>Portada</th>
                      <th style={{ padding: '10px 10px', whiteSpace: 'nowrap' }}>Sol.</th>
                    </>
                  ) : null}
                  <th style={{ padding: '10px 10px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map(r => {
                  const autorLabel = comunidadAutorLabel(r, namesById);
                  const esN8n = isComunidadEventoN8n(r);
                  const trackRow =
                    showSeguimiento &&
                    (tab === 'pendientes' ||
                      tab === 'eventos_n8n' ||
                      (tab === 'feed' && r.destacada_solicitud_pendiente && !r.destacada));
                  const sinLeer = trackRow && seguimiento.isUnread(r.id);
                  const carouselEligible = carouselFeed && isComunidadCarouselEligible(r);
                  const fechaEventoLabel = showFechaEventoCol ? formatFechaEventoRow(r) : null;
                  const carouselSent = showCarouselSentCol ? comunidadTelegramCarouselSentInfo(r) : null;
                  const dupCheck = getComunidadDuplicateCheck(r);
                  const dupAlert = comunidadHasDuplicateAlert(dupCheck);
                  return (
                    <tr
                      key={r.id}
                      style={adminSeguimientoRowStyle(sinLeer, {
                        borderBottom: '1px solid rgba(255,255,255,0.06)',
                        color: '#fff',
                        ...(carouselSel.isSelected(r.id) ? { background: 'rgba(200,168,75,0.06)' } : {}),
                      })}
                    >
                      {carouselFeed ? (
                        <td style={{ padding: '10px 10px', verticalAlign: 'top' }}>
                          {carouselEligible ? (
                            <input
                              type="checkbox"
                              aria-label={`Seleccionar ${r.title}`}
                              checked={carouselSel.isSelected(r.id)}
                              disabled={!carouselSel.isSelected(r.id) && carouselSel.atMax}
                              onChange={() => carouselSel.toggle(r.id)}
                            />
                          ) : (
                            <span
                              style={{ color: 'rgba(255,255,255,0.25)', fontSize: 10 }}
                              title={
                                r.publicacion_tipo !== TIPO_EVENTO
                                  ? 'Solo eventos en vivo'
                                  : 'Sin imagen'
                              }
                            >
                              —
                            </span>
                          )}
                        </td>
                      ) : null}
                      {showSeguimiento ? (
                        <td style={{ padding: '10px 10px', verticalAlign: 'top' }}>
                          {trackRow ? (
                            <AdminLeidaCell
                              leida={seguimiento.readIds.has(r.id)}
                              busy={seguimiento.readBusyId === r.id}
                              onChange={checked => seguimiento.setReadState(r.id, checked)}
                            />
                          ) : (
                            <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 10 }}>—</span>
                          )}
                        </td>
                      ) : null}
                      <td style={{ padding: '10px 10px', maxWidth: 200, fontWeight: sinLeer ? 800 : 650, verticalAlign: 'top' }}>
                        {sinLeer ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <AdminAlertDot title="Sin leer" size={7} />
                            <span>{r.title}</span>
                          </span>
                        ) : (
                          r.title
                        )}
                      </td>
                      {showEstadoCol ? (
                        <td style={{ padding: '10px 10px', verticalAlign: 'top' }}>
                          {(() => {
                            const est = comunidadModeracionEstado(r);
                            return (
                              <span
                                style={comunidadModeracionEstadoPillStyle(est.key, {
                                  gold: C.gold,
                                  green: C.green,
                                  danger: C.danger,
                                })}
                                title={est.title}
                              >
                                {est.label}
                              </span>
                            );
                          })()}
                        </td>
                      ) : null}
                      <td style={{ padding: '10px 10px', verticalAlign: 'top', whiteSpace: 'nowrap', color: C.gold }}>
                        {r.publicacion_tipo || '—'}
                      </td>
                      <td style={{ padding: '10px 10px', maxWidth: 160, verticalAlign: 'top', fontSize: 10, color: 'rgba(255,255,255,0.55)' }}>
                        {sitioBreve(r)}
                      </td>
                      <td style={{ padding: '10px 10px', verticalAlign: 'top', fontSize: 10, color: esN8n ? C.gold : undefined }}>
                        {esN8n && r.fuente_url ? (
                          <a
                            href={r.fuente_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={r.fuente_url}
                            style={{ color: C.gold, textDecoration: 'underline', textUnderlineOffset: 2 }}
                          >
                            {autorLabel}
                          </a>
                        ) : (
                          autorLabel
                        )}
                        {esN8n ? (
                          <span style={{ display: 'block', fontSize: 9, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>
                            n8n automático
                          </span>
                        ) : null}
                        {r.external_key ? (
                          <span style={{ display: 'block', fontSize: 9, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>
                            key: {String(r.external_key).slice(0, 24)}
                            {String(r.external_key).length > 24 ? '…' : ''}
                          </span>
                        ) : null}
                      </td>
                      {showDupCol ? (
                        <td style={{ padding: '10px 10px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                          {r.publicacion_tipo !== TIPO_EVENTO ? (
                            <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 10 }}>—</span>
                          ) : dupCheck ? (
                            <button
                              type="button"
                              onClick={() => (dupAlert ? abrirCompararDuplicado(r) : null)}
                              title={
                                dupAlert
                                  ? 'Comparar con posible duplicado'
                                  : dupCheck.dismissed
                                    ? 'Duplicado descartado por admin'
                                    : 'Sin duplicados detectados'
                              }
                              style={{
                                ...btnMini,
                                cursor: dupAlert ? 'pointer' : 'default',
                                borderColor: `${comunidadDuplicateStatusColor(dupCheck.status)}66`,
                                color: comunidadDuplicateStatusColor(dupCheck.status),
                                opacity: dupCheck.dismissed ? 0.65 : 1,
                              }}
                            >
                              {dupCheck.dismissed ? 'Descartado' : comunidadDuplicateStatusLabel(dupCheck.status)}
                            </button>
                          ) : (
                            <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)' }}>…</span>
                          )}
                        </td>
                      ) : null}
                      {showFechaEventoCol ? (
                        <td
                          style={{
                            padding: '10px 10px',
                            whiteSpace: 'nowrap',
                            verticalAlign: 'top',
                            color: fechaEventoLabel === '—' ? 'rgba(255,255,255,0.35)' : C.gold,
                          }}
                          title={
                            r?.cuerpo?.fecha
                              ? String(r.cuerpo.fecha)
                              : r?.fuente_fecha
                                ? String(r.fuente_fecha)
                                : undefined
                          }
                        >
                          {fechaEventoLabel}
                        </td>
                      ) : null}
                      <td style={{ padding: '10px 10px', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                        {formatFecha(r.created_at)}
                      </td>
                      {tab === 'feed' ? (
                        <>
                          <td style={{ padding: '10px 10px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                            {carouselSent?.label === '—' ? (
                              <span style={{ color: 'rgba(255,255,255,0.3)' }}>—</span>
                            ) : carouselSent?.sent ? (
                              <span style={{ color: C.green }} title={formatFecha(carouselSent.at)}>
                                Sí
                              </span>
                            ) : (
                              <span style={{ color: 'rgba(255,255,255,0.4)' }}>No</span>
                            )}
                          </td>
                          <td style={{ padding: '10px 10px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                            {r.destacada ? (
                              <span style={{ color: C.green }}>Sí</span>
                            ) : (
                              <span style={{ color: 'rgba(255,255,255,0.35)' }}>No</span>
                            )}
                            {r.destacada_grooves_cobrados != null ? (
                              <span style={{ display: 'block', fontSize: 9, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>
                                −{r.destacada_grooves_cobrados} G
                              </span>
                            ) : null}
                          </td>
                          <td style={{ padding: '10px 10px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                            {r.destacada_solicitud_pendiente ? (
                              <span style={{ color: C.gold }}>Pendiente</span>
                            ) : (
                              <span style={{ color: 'rgba(255,255,255,0.35)' }}>—</span>
                            )}
                          </td>
                        </>
                      ) : null}
                      <td style={{ padding: '10px 10px', verticalAlign: 'top' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {(tab === 'pendientes' || tab === 'eventos_n8n') ? (
                            <button
                              type="button"
                              disabled={busyId === r.id || (aiLoading && aiRow?.id === r.id)}
                              onClick={() => analizarConIa(r)}
                              title={
                                tab === 'eventos_n8n'
                                  ? 'Revisión asistida vía n8n (opcional, bajo demanda)'
                                  : 'Moderación asistida vía n8n (pendientes usuario)'
                              }
                              style={{
                                ...btnMini,
                                borderColor: 'rgba(83,74,183,0.45)',
                                color: 'rgba(200,190,255,0.95)',
                              }}
                            >
                              Análisis IA
                            </button>
                          ) : null}
                          {showDupCol && dupAlert ? (
                            <button
                              type="button"
                              disabled={busyId === r.id}
                              onClick={() => abrirCompararDuplicado(r)}
                              title="Comparar lado a lado con posible duplicado"
                              style={{
                                ...btnMini,
                                borderColor: 'rgba(226,75,74,0.45)',
                                color: '#f0a8a7',
                              }}
                            >
                              Comparar dup.
                            </button>
                          ) : null}
                          {eventoConEnlaceEntradas(r) ? (
                            <>
                              <button
                                type="button"
                                disabled={busyId === r.id || (entradaLoading && entradaRow?.id === r.id)}
                                onClick={() => verificarEnlaceEntradas(r)}
                                title="Comprueba URL de entradas (ticketera conocida / IA)"
                                style={{
                                  ...btnMini,
                                  borderColor: r.entradas_verificadas ? 'rgba(29,158,117,0.45)' : 'rgba(239,159,39,0.45)',
                                  color: r.entradas_verificadas ? C.green : C.warning,
                                }}
                              >
                                {r.entradas_verificadas ? 'Entradas OK' : 'Verificar entradas'}
                              </button>
                              {!r.entradas_verificadas ? (
                                <button
                                  type="button"
                                  disabled={busyId === r.id}
                                  onClick={() => aprobarEntradaManual(r)}
                                  style={{ ...btnMini, borderColor: 'rgba(29,158,117,0.35)', color: C.green }}
                                >
                                  Aprobar enlace
                                </button>
                              ) : null}
                            </>
                          ) : null}
                          <button
                            type="button"
                            disabled={busyId === r.id || previewSaving}
                            onClick={() => abrirVistaPrevia(r)}
                            style={btnMini}
                          >
                            Vista previa
                          </button>
                          <button
                            type="button"
                            disabled={busyId === r.id || previewSaving}
                            onClick={() => abrirVistaPrevia(r, { editar: true })}
                            style={{ ...btnMini, borderColor: 'rgba(29,158,117,0.35)', color: C.green }}
                          >
                            Editar
                          </button>
                          {tab === 'feed' ? (
                            <>
                              <button
                                type="button"
                                disabled={busyId === r.id}
                                onClick={() => toggleDestacada(r)}
                                style={{
                                  ...btnMini,
                                  borderColor: r.destacada ? 'rgba(29,158,117,0.45)' : 'rgba(200,168,75,0.35)',
                                  color: r.destacada ? C.green : C.gold,
                                }}
                              >
                                {r.destacada ? 'Quitar portada' : 'Destacar'}
                              </button>
                              {r.destacada_solicitud_pendiente && !r.destacada ? (
                                <button
                                  type="button"
                                  disabled={busyId === r.id}
                                  onClick={() => descartarSolicitudDestacado(r)}
                                  style={{ ...btnMini, borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.55)' }}
                                >
                                  Descartar sol.
                                </button>
                              ) : null}
                            </>
                          ) : null}
                          {tab === 'pendientes' ? (
                            <>
                              <button
                                type="button"
                                disabled={busyId === r.id}
                                onClick={() => aprobarAlFeed(r)}
                                style={{ ...btnMini, borderColor: 'rgba(29,158,117,0.45)', color: C.green }}
                              >
                                Aprobar al feed
                              </button>
                              <button type="button" disabled={busyId === r.id} onClick={() => abrirRechazo(r)} style={btnMini}>
                                Rechazar
                              </button>
                            </>
                          ) : null}
                          {tab === 'eventos_n8n' ? (
                            <>
                              <button
                                type="button"
                                disabled={busyId === r.id || uploadingCartelId === r.id}
                                style={{ ...btnMini, opacity: uploadingCartelId === r.id ? 0.55 : 1 }}
                                onClick={() => subirCartelClick(r)}
                              >
                                {uploadingCartelId === r.id ? 'Subiendo…' : 'Subir cartel'}
                              </button>
                              <button
                                type="button"
                                disabled={busyId === r.id}
                                onClick={() => aprobarAlFeed(r)}
                                style={{ ...btnMini, borderColor: 'rgba(29,158,117,0.45)', color: C.green }}
                              >
                                Aprobar al feed
                              </button>
                              <button
                                type="button"
                                disabled={busyId === r.id}
                                onClick={() => eliminar(r)}
                                style={{ ...btnMini, borderColor: 'rgba(226,75,74,0.45)', color: C.danger }}
                              >
                                Eliminar borrador
                              </button>
                            </>
                          ) : null}
                          {tab === 'feed' ? (
                            <>
                              <button
                                type="button"
                                disabled={busyId === r.id}
                                onClick={() => setSocialKitOpen({ contentId: r.id, titleHint: r.title })}
                                style={{ ...btnMini, borderColor: 'rgba(120,160,255,0.45)', color: 'rgba(180,200,255,0.95)' }}
                              >
                                Redes
                              </button>
                              <button type="button" disabled={busyId === r.id} onClick={() => retirarDelFeed(r)} style={btnMini}>
                                Retirar del feed
                              </button>
                              <button
                                type="button"
                                disabled={busyId === r.id}
                                onClick={() => eliminar(r)}
                                style={{ ...btnMini, borderColor: 'rgba(226,75,74,0.45)', color: C.danger }}
                              >
                                Eliminar
                              </button>
                            </>
                          ) : null}
                          {tab === 'ocultas' ? (
                            <>
                              {esN8n ? (
                                <div style={{ width: '100%', fontSize: 9, color: 'rgba(255,200,120,0.85)', marginBottom: 4 }}>
                                  Borrador n8n antiguo en Ocultas — elimínalo para limpiar (no usa el flujo de rechazo de usuarios).
                                </div>
                              ) : (
                                <div style={{ width: '100%', fontSize: 9, color: 'rgba(255,255,255,0.35)', marginBottom: 4 }}>
                                  {r.moderacion_rechazo_codigo ? (
                                    <span style={{ display: 'block', color: 'rgba(255,200,120,0.85)', marginBottom: 2 }}>
                                      Tipo: {etiquetaMotivoRechazo(r.moderacion_rechazo_codigo)}
                                    </span>
                                  ) : null}
                                  {r.hidden_reason || 'Sin motivo'}
                                  {r.moderacion_rechazo_detalle ? (
                                    <span style={{ display: 'block', marginTop: 3, color: 'rgba(255,255,255,0.45)' }}>
                                      Detalle: {r.moderacion_rechazo_detalle}
                                    </span>
                                  ) : null}
                                </div>
                              )}
                              {!esN8n ? (
                                <button
                                  type="button"
                                  disabled={busyId === r.id}
                                  onClick={() => quitarOcultacion(r)}
                                  style={{ ...btnMini, borderColor: 'rgba(29,158,117,0.45)', color: C.green }}
                                >
                                  Restaurar
                                </button>
                              ) : null}
                              {!esN8n && !r.aprobada ? (
                                <button
                                  type="button"
                                  disabled={busyId === r.id}
                                  onClick={() => aprobarAlFeed(r)}
                                  style={{ ...btnMini, borderColor: 'rgba(29,158,117,0.45)', color: C.green }}
                                >
                                  Aprobar al feed
                                </button>
                              ) : null}
                              <button
                                type="button"
                                disabled={busyId === r.id}
                                onClick={() => eliminar(r)}
                                style={{ ...btnMini, borderColor: 'rgba(226,75,74,0.45)', color: C.danger }}
                              >
                                {esN8n ? 'Eliminar borrador' : 'Eliminar'}
                              </button>
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!rows.length ? (
              <div style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>
                {tab === 'eventos_n8n'
                  ? 'No hay borradores n8n pendientes. Los eventos del workflow aparecerán aquí al ingestarse.'
                  : 'No hay entradas en esta pestaña.'}
              </div>
            ) : null}
          </div>
        )}
      </DCard>

      {previewRow && previewDraft ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Vista previa publicación Comunidad"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            background: 'rgba(10,10,15,0.72)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '24px 16px',
            overflowY: 'auto',
          }}
          onClick={() => !previewSaving && cerrarVistaPrevia()}
        >
          <div
            role="presentation"
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 680,
              background: '#fff',
              borderRadius: 14,
              boxShadow: '0 22px 60px rgba(0,0,0,0.35)',
              marginTop: '4vh',
              marginBottom: 40,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '16px 18px',
                borderBottom: '1px solid rgba(0,0,0,0.06)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: '#b8922e',
                    letterSpacing: '0.08em',
                    marginBottom: 6,
                  }}
                >
                  {comunidadTipoPublicacionLabel(
                    previewEditMode ? previewDraft.tipo : previewRow.publicacion_tipo
                  )}
                </div>
                {previewEditMode ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <label style={lblModal}>Título</label>
                    <input
                      value={previewDraft.titulo}
                      onChange={e => setPreviewDraft(d => ({ ...d, titulo: e.target.value }))}
                      placeholder="Título"
                      style={inpModal}
                    />
                    <label style={lblModal}>Tipo de publicación</label>
                    <select
                      value={previewDraft.tipo}
                      onChange={e => {
                        const t = e.target.value;
                        setPreviewDraft(d => ({
                          ...d,
                          tipo: t,
                          ...(t !== TIPO_EVENTO ? { entradaTipo: 'libre', entradaDetalle: '', entradaEnlace: '' } : {}),
                        }));
                      }}
                      style={{ ...inpModal, cursor: 'pointer' }}
                    >
                      {COMUNIDAD_TIPOS_PUBLICACION.map(({ value, label }) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                    <ComunidadMusicGenresField
                      variant="light"
                      mode={previewDraft.musicGenreMode || 'specific'}
                      genres={previewDraft.musicGenres || []}
                      onChange={patch => setPreviewDraft(d => ({ ...d, ...patch }))}
                    />
                  </div>
                ) : (
                  <h2
                    style={{
                      fontSize: '1.25rem',
                      fontWeight: 750,
                      margin: 0,
                      fontFamily: 'Georgia,serif',
                      lineHeight: 1.35,
                      color: '#111',
                    }}
                  >
                    {previewRow.title}
                  </h2>
                )}
                <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                  {formatFecha(previewRow.created_at)}
                  <span style={{ marginLeft: 8 }}>
                    {isComunidadEventoN8n(previewRow) ? 'n8n · ' : ''}
                    {comunidadFuenteDominio(previewRow.fuente_url) || (isComunidadEventoN8n(previewRow) ? 'automático' : comunidadAutorLabel(previewRow, namesById))}
                  </span>
                  {isComunidadEventoN8n(previewRow) && previewRow.fuente_url ? (
                    <a
                      href={previewRow.fuente_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'block', marginTop: 6, color: C.gold, fontSize: 11 }}
                    >
                      Fuente original ({comunidadFuenteDominio(previewRow.fuente_url)}) →
                    </a>
                  ) : isComunidadEventoN8n(previewRow) ? (
                    <span style={{ display: 'block', marginTop: 6, fontSize: 11, color: '#b45309' }}>
                      Sin fuente_url — añádela en n8n (campo Tavily «url») para ver el origen.
                    </span>
                  ) : null}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {!previewEditMode ? (
                  <button
                    type="button"
                    disabled={previewSaving}
                    onClick={() => setPreviewEditMode(true)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 10,
                      border: '1px solid rgba(0,0,0,0.1)',
                      background: '#111',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    Editar
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      disabled={previewSaving}
                      onClick={() => {
                        setPreviewEditMode(false);
                        setPreviewDraft(comunidadDbRowToAdminForm(previewRow));
                      }}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 10,
                        border: '1px solid rgba(0,0,0,0.1)',
                        background: '#fafafa',
                        color: '#111',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      disabled={previewSaving}
                      onClick={() => guardarEdicionVistaPrevia()}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 10,
                        border: '1px solid rgba(29,158,117,0.35)',
                        background: previewSaving ? '#e9f7f1' : 'rgba(29,158,117,0.12)',
                        color: '#1d9e75',
                        cursor: previewSaving ? 'wait' : 'pointer',
                        fontSize: 12,
                        fontWeight: 800,
                      }}
                    >
                      {previewSaving ? 'Guardando…' : 'Guardar'}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  disabled={previewSaving}
                  onClick={() => cerrarVistaPrevia()}
                  aria-label="Cerrar"
                  style={{
                    flexShrink: 0,
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    border: '1px solid rgba(0,0,0,0.1)',
                    background: '#fafafa',
                    cursor: previewSaving ? 'wait' : 'pointer',
                    fontSize: 18,
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </div>
            </div>
            {(() => {
              const cBody =
                previewRow.cuerpo && typeof previewRow.cuerpo === 'object' ? previewRow.cuerpo : {};
              const imgRead = String(cBody.imagenUrl || '').trim();
              const imgEdit = String(previewDraft.imagenPreview || '').trim();
              const imgShow = previewEditMode ? imgEdit : imgRead;
              const imagenExterna = imgRead && !isComunidadImagenInOurStorage(imgRead);
              return (
                <>
                  {imagenExterna && !previewEditMode ? (
                    <div
                      style={{
                        padding: '10px 14px',
                        background: 'rgba(184,134,11,0.12)',
                        borderBottom: '1px solid rgba(184,134,11,0.25)',
                        fontSize: 11,
                        lineHeight: 1.45,
                        color: '#6b4e00',
                      }}
                    >
                      El cartel apunta a una URL externa que puede bloquear la descarga al aprobar (403).{' '}
                      <strong>Sube el cartel</strong> desde tu equipo antes de «Aprobar al feed».
                      <div style={{ marginTop: 8 }}>
                        <button
                          type="button"
                          disabled={uploadingCartelId === previewRow.id}
                          style={{ ...btnMini, borderColor: 'rgba(184,134,11,0.45)', color: '#8a6914' }}
                          onClick={() => subirCartelClick(previewRow)}
                        >
                          {uploadingCartelId === previewRow.id ? 'Subiendo…' : 'Subir cartel'}
                        </button>
                      </div>
                    </div>
                  ) : null}
                  {imgShow ? (
                <div
                  style={{
                    width: '100%',
                    height: 200,
                    position: 'relative',
                    overflow: 'hidden',
                    background: '#0a0a0f',
                    lineHeight: 0,
                  }}
                >
                  <img
                    src={imgShow}
                    alt=""
                    decoding="async"
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      objectPosition: 'center center',
                      display: 'block',
                    }}
                  />
                </div>
              ) : null}
                </>
              );
            })()}
            <div style={{ padding: '18px 20px 24px', fontSize: 14, lineHeight: 1.6, color: '#333' }}>
              {previewEditMode ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={lblModal}>País</label>
                      <select
                        value={previewDraft.pais}
                        onChange={e =>
                          setPreviewDraft(d => ({
                            ...d,
                            pais: e.target.value,
                            provincia: '',
                            provinciaTexto: '',
                            ciudad: '',
                          }))
                        }
                        style={{ ...inpModal, cursor: 'pointer' }}
                      >
                        {PAISES_COMUN.map(({ v, l }) => (
                          <option key={v} value={v}>
                            {l}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={lblModal}>Código postal</label>
                      <input
                        value={previewDraft.cp}
                        onChange={e => setPreviewDraft(d => ({ ...d, cp: e.target.value }))}
                        style={inpModal}
                      />
                    </div>
                  </div>
                  {previewDraft.pais === 'ES' ? (
                    <div>
                      <label style={lblModal}>Provincia (código o texto guardado)</label>
                      <input
                        value={previewDraft.provincia}
                        onChange={e => setPreviewDraft(d => ({ ...d, provincia: e.target.value }))}
                        style={inpModal}
                        placeholder="Ej. código INE o nombre"
                      />
                    </div>
                  ) : (
                    <div>
                      <label style={lblModal}>Provincia o región</label>
                      <input
                        value={previewDraft.provinciaTexto}
                        onChange={e => setPreviewDraft(d => ({ ...d, provinciaTexto: e.target.value }))}
                        style={inpModal}
                      />
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={lblModal}>Ciudad o municipio</label>
                      <input
                        value={previewDraft.ciudad}
                        onChange={e => setPreviewDraft(d => ({ ...d, ciudad: e.target.value }))}
                        style={inpModal}
                      />
                    </div>
                    <div>
                      <label style={lblModal}>Calle / vía</label>
                      <input
                        value={previewDraft.via}
                        onChange={e => setPreviewDraft(d => ({ ...d, via: e.target.value }))}
                        style={inpModal}
                      />
                    </div>
                  </div>
                  <div>
                    <label style={lblModal}>Enlace Google Maps (opcional)</label>
                    <input
                      value={previewDraft.mapsUrl}
                      onChange={e => setPreviewDraft(d => ({ ...d, mapsUrl: e.target.value }))}
                      style={inpModal}
                      placeholder="https://maps.google.com/…"
                    />
                  </div>
                  <div>
                    <label style={lblModal}>Fecha u horario</label>
                    <input
                      value={previewDraft.fecha}
                      onChange={e => setPreviewDraft(d => ({ ...d, fecha: e.target.value }))}
                      style={inpModal}
                      placeholder="ej. 12 Jul · 21:00h"
                    />
                  </div>
                  <div>
                    <label style={lblModal}>Descripción / notas</label>
                    <textarea
                      value={previewDraft.notas ?? ''}
                      onChange={e => setPreviewDraft(d => ({ ...d, notas: e.target.value }))}
                      rows={5}
                      style={{ ...inpModal, resize: 'vertical', minHeight: 100, fontFamily: 'inherit' }}
                      placeholder="Descripción del evento, artistas, horarios, etc."
                    />
                  </div>
                  {isComunidadEventoN8n(previewRow) ? (
                    <div>
                      <label style={lblModal}>URL fuente original (web del evento)</label>
                      <input
                        value={previewDraft.fuenteUrl ?? ''}
                        onChange={e => setPreviewDraft(d => ({ ...d, fuenteUrl: e.target.value }))}
                        style={inpModal}
                        placeholder="https://…"
                      />
                    </div>
                  ) : null}
                  <div>
                    <label style={lblModal}>URL imagen / cartel (opcional)</label>
                    <input
                      value={previewDraft.imagenPreview || ''}
                      onChange={e => setPreviewDraft(d => ({ ...d, imagenPreview: e.target.value || null }))}
                      style={inpModal}
                    />
                  </div>
                  {previewDraft.tipo === TIPO_EVENTO ? (
                    <>
                      <div>
                        <label style={lblModal}>Tipo de entrada</label>
                        <select
                          value={previewDraft.entradaTipo}
                          onChange={e => setPreviewDraft(d => ({ ...d, entradaTipo: e.target.value, entradaDetalle: '' }))}
                          style={{ ...inpModal, cursor: 'pointer' }}
                        >
                          {ENTRADA_TIPO_OPCIONES.map(({ v, l }) => (
                            <option key={v} value={v}>
                              {l}
                            </option>
                          ))}
                        </select>
                      </div>
                      {['precio_fijo', 'consumicion', 'otros'].includes(previewDraft.entradaTipo) ? (
                        <div>
                          <label style={lblModal}>Detalle precio / texto</label>
                          <input
                            value={previewDraft.entradaDetalle}
                            onChange={e => setPreviewDraft(d => ({ ...d, entradaDetalle: e.target.value }))}
                            style={inpModal}
                          />
                        </div>
                      ) : null}
                      <div>
                        <label style={lblModal}>Enlace venta / info entradas (opcional)</label>
                        <input
                          value={previewDraft.entradaEnlace}
                          onChange={e => setPreviewDraft(d => ({ ...d, entradaEnlace: e.target.value }))}
                          style={inpModal}
                        />
                      </div>
                    </>
                  ) : null}
                </div>
              ) : (
                (() => {
                  const c = previewRow.cuerpo && typeof previewRow.cuerpo === 'object' ? previewRow.cuerpo : {};
                  const maps = String(c.mapsUrl || '').trim();
                  const notas = String(c.notas || '').trim();
                  const isEv = previewRow.publicacion_tipo === TIPO_EVENTO;
                  return (
                    <>
                      <p style={{ margin: '0 0 10px', color: '#555' }}>
                        <strong>Ubicación:</strong> {String(c.place || '').trim() || sitioBreve(previewRow)}
                      </p>
                      {c.fecha ? (
                        <p style={{ margin: '0 0 10px', color: '#555' }}>
                          <strong>Fecha:</strong> {c.fecha}
                        </p>
                      ) : null}
                      {maps ? (
                        <p style={{ margin: '0 0 10px' }}>
                          <strong>Maps:</strong>{' '}
                          <a href={maps} target="_blank" rel="noopener noreferrer" style={{ color: '#1d6b9e', wordBreak: 'break-all' }}>
                            {maps}
                          </a>
                        </p>
                      ) : null}
                      {isEv ? (
                        <p style={{ margin: '0 0 10px', color: '#555' }}>
                          <strong>Entradas:</strong> {entradaEtiqueta(previewRow.entrada_tipo)}
                          {previewRow.entrada_precio ? ` · ${previewRow.entrada_precio}` : ''}
                          {previewRow.entrada_enlace ? (
                            <>
                              {' · '}
                              <a
                                href={String(previewRow.entrada_enlace).trim()}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#1d6b9e', wordBreak: 'break-all' }}
                              >
                                enlace
                              </a>
                              {' · '}
                              <span style={{ color: previewRow.entradas_verificadas ? '#1d9e75' : '#b8860b', fontWeight: 700 }}>
                                {previewRow.entradas_verificadas ? 'Verificado' : 'Pendiente verificación'}
                              </span>
                            </>
                          ) : null}
                        </p>
                      ) : (
                        <p style={{ margin: '0 0 10px', color: '#555' }}>
                          <strong>En feed como:</strong> {tipoSitioCorto(previewRow.publicacion_tipo)}
                        </p>
                      )}
                      {notas ? (
                        <div style={{ marginTop: 12, padding: 12, borderRadius: 10, background: '#fafafa', border: '1px solid rgba(0,0,0,0.06)' }}>
                          <div style={{ fontSize: 11, fontWeight: 800, color: '#666', marginBottom: 6 }}>Notas</div>
                          <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, color: '#333' }}>{notas}</div>
                        </div>
                      ) : (
                        <p style={{ margin: 0, color: '#aaa', fontSize: 13 }}>Sin notas adicionales.</p>
                      )}
                    </>
                  );
                })()
              )}
            </div>
          </div>
        </div>
      ) : null}

      {rejectModal ? (
        <>
          <div
            role="presentation"
            onClick={() => !busyId && cerrarRechazoModal()}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1990 }}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="comunidad-rechazo-title"
            onClick={e => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1995,
              width: 'min(440px, 94vw)',
              maxHeight: 'min(88vh, 620px)',
              overflowY: 'auto',
              background: 'linear-gradient(180deg, #1a1d28 0%, #12141c 100%)',
              borderRadius: 14,
              border: '1px solid rgba(200,168,75,0.28)',
              boxShadow: '0 24px 64px rgba(0,0,0,0.55)',
              padding: '18px 18px 14px',
            }}
          >
            <h3 id="comunidad-rechazo-title" style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 800, color: '#fff' }}>
              Rechazar publicación
            </h3>
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', margin: '0 0 14px', lineHeight: 1.45 }}>
              <strong style={{ color: 'rgba(255,255,255,0.75)' }}>{rejectModal.title}</strong>
              <span style={{ display: 'block', marginTop: 6 }}>
                Se ocultará del público y el autor recibirá una notificación con el motivo (y detalle si lo indicas).
              </span>
            </p>
            <label style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Tipo de incumplimiento
            </label>
            <select
              value={rejectCodigo}
              onChange={e => setRejectCodigo(e.target.value)}
              style={{
                width: '100%',
                marginTop: 6,
                marginBottom: 12,
                padding: '10px 12px',
                borderRadius: 10,
                border: '1px solid rgba(255,255,255,0.12)',
                background: 'rgba(0,0,0,0.25)',
                color: '#fff',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              {COMUNIDAD_RECHAZO_MOTIVOS.map(m => (
                <option key={m.codigo} value={m.codigo} style={{ background: '#1a1d28', color: '#fff' }}>
                  {m.etiqueta}
                </option>
              ))}
            </select>
            <label style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Detalle para el autor (opcional; obligatorio si el motivo lo requiere)
            </label>
            <textarea
              value={rejectDetalle}
              onChange={e => setRejectDetalle(e.target.value)}
              rows={4}
              placeholder="Ej. falta la fecha del evento, enlace roto, lenguaje inadecuado en notas…"
              style={{
                width: '100%',
                marginTop: 6,
                marginBottom: 14,
                padding: '10px 12px',
                borderRadius: 10,
                border: '1px solid rgba(255,255,255,0.12)',
                background: 'rgba(0,0,0,0.25)',
                color: '#fff',
                fontSize: 12,
                resize: 'vertical',
                minHeight: 88,
                boxSizing: 'border-box',
                fontFamily: 'inherit',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
              <BtnGhost type="button" style={{ fontSize: 11 }} onClick={() => !busyId && cerrarRechazoModal()} disabled={!!busyId}>
                Cancelar
              </BtnGhost>
              <button
                type="button"
                disabled={!!busyId}
                onClick={() => confirmarRechazo()}
                style={{
                  padding: '8px 14px',
                  borderRadius: 10,
                  border: '1px solid rgba(226,75,74,0.45)',
                  background: busyId ? 'rgba(226,75,74,0.12)' : 'rgba(226,75,74,0.2)',
                  color: C.danger,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: busyId ? 'wait' : 'pointer',
                }}
              >
                {busyId === rejectModal.id ? 'Aplicando…' : 'Rechazar y notificar'}
              </button>
            </div>
          </div>
        </>
      ) : null}

      {aiRow ? (
        <>
          <div
            role="presentation"
            onClick={cerrarAiModal}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 600 }}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="comunidad-ai-title"
            onClick={e => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 610,
              width: 'min(480px, 94vw)',
              maxHeight: 'min(85vh, 640px)',
              overflowY: 'auto',
              background: 'linear-gradient(180deg, #1a1d28 0%, #12141c 100%)',
              borderRadius: 14,
              border: '1px solid rgba(200,168,75,0.28)',
              boxShadow: '0 24px 64px rgba(0,0,0,0.55)',
              padding: '18px 18px 14px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
              <h3 id="comunidad-ai-title" style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#fff' }}>
                Análisis IA
              </h3>
              <button
                type="button"
                onClick={cerrarAiModal}
                style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 20, cursor: 'pointer', lineHeight: 1, padding: 2 }}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>
            <p
              style={{
                fontSize: 11,
                color: 'rgba(255,255,255,0.5)',
                margin: '0 0 14px',
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={aiRow.title}
            >
              {aiRow.title}
            </p>
            {aiLoading ? (
              <div style={{ padding: '20px 0', textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>Analizando…</div>
            ) : aiError ? (
              <div style={{ padding: 10, borderRadius: 8, background: 'rgba(226,75,74,0.12)', border: '0.5px solid rgba(226,75,74,0.35)', color: C.danger, fontSize: 12 }}>
                {aiError}
              </div>
            ) : aiScreening ? (
              (() => {
                const actionColor = aiSuggestedActionColor(aiScreening.suggested_action);
                const checks = resolveAiChecks(aiScreening);
                const verdict = String(aiScreening.verdict_line || '').trim();
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          display: 'inline-block',
                          padding: '8px 22px',
                          borderRadius: 999,
                          fontSize: 15,
                          fontWeight: 900,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          color: actionColor,
                          background: `${actionColor}18`,
                          border: `1.5px solid ${actionColor}`,
                        }}
                      >
                        {aiSuggestedActionLabel(aiScreening.suggested_action)}
                      </div>
                      {verdict ? (
                        <p style={{ margin: '8px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.72)', lineHeight: 1.4 }}>
                          {verdict}
                        </p>
                      ) : null}
                    </div>

                    {checks.length > 0 ? (
                      <div
                        style={{
                          borderRadius: 10,
                          border: '1px solid rgba(255,255,255,0.08)',
                          overflow: 'hidden',
                        }}
                      >
                        {checks.map((check, i) => {
                          const meta = aiCheckStatusMeta(check.status);
                          const showDetail = check.detail && check.status !== 'ok';
                          return (
                            <div
                              key={check.id || i}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                padding: '7px 10px',
                                borderTop: i > 0 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                                background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                              }}
                            >
                              <span
                                style={{
                                  flexShrink: 0,
                                  width: 20,
                                  height: 20,
                                  borderRadius: 5,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 11,
                                  fontWeight: 900,
                                  color: meta.color,
                                  background: `${meta.color}22`,
                                }}
                                aria-hidden
                              >
                                {meta.icon}
                              </span>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div
                                  style={{
                                    fontSize: 12,
                                    fontWeight: check.status === 'ok' ? 500 : 700,
                                    color: check.status === 'ok' ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.9)',
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {check.label}
                                </div>
                                {showDetail ? (
                                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 1, lineHeight: 1.3 }}>
                                    {check.detail}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}

                    {aiScreening.risk && aiScreening.risk !== 'low' ? (
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.38)', textAlign: 'center' }}>
                        Riesgo{' '}
                        <strong style={{ color: riskColor(aiScreening.risk), textTransform: 'uppercase' }}>
                          {aiScreening.risk}
                        </strong>
                      </div>
                    ) : null}
                  </div>
                );
              })()
            ) : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <BtnGhost type="button" style={{ fontSize: 11 }} onClick={cerrarAiModal}>
                Cerrar
              </BtnGhost>
            </div>
          </div>
        </>
      ) : null}

      {entradaRow ? (
        <>
          <div
            role="presentation"
            onClick={cerrarEntradaModal}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 600 }}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="comunidad-entrada-title"
            onClick={e => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 610,
              width: 'min(480px, 94vw)',
              maxHeight: 'min(85vh, 640px)',
              overflowY: 'auto',
              background: 'linear-gradient(180deg, #1a1d28 0%, #12141c 100%)',
              borderRadius: 14,
              border: '1px solid rgba(200,168,75,0.28)',
              boxShadow: '0 24px 64px rgba(0,0,0,0.55)',
              padding: '18px 18px 14px',
            }}
          >
            <h3 id="comunidad-entrada-title" style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 800, color: '#fff' }}>
              Verificación enlace de entradas
            </h3>
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', margin: '0 0 12px', lineHeight: 1.45 }}>
              <strong style={{ color: 'rgba(255,255,255,0.65)' }}>{entradaRow.title}</strong>
            </p>
            {entradaLoading ? (
              <div style={{ padding: '20px 0', textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>Comprobando URL…</div>
            ) : entradaError ? (
              <div style={{ padding: 10, borderRadius: 8, background: 'rgba(226,75,74,0.12)', border: '0.5px solid rgba(226,75,74,0.35)', color: C.danger, fontSize: 12 }}>
                {entradaError}
              </div>
            ) : entradaScreening ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Origen: {entradaScreening.source}</div>
                <div style={{ fontSize: 13, fontWeight: 800, color: entradaScreening.verified ? C.green : C.warning }}>
                  {entradaScreening.verified ? 'Enlace verificado' : 'No verificado automáticamente'}
                  <span style={{ fontWeight: 600, marginLeft: 8, color: C.gold }}>{entradaScreening.confidence}%</span>
                </div>
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.82)', margin: 0, lineHeight: 1.5 }}>{entradaScreening.summary}</p>
                {Array.isArray(entradaScreening.reasons) && entradaScreening.reasons.length > 0 ? (
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11, color: 'rgba(255,255,255,0.7)', lineHeight: 1.45 }}>
                    {entradaScreening.reasons.map((x, i) => (
                      <li key={i}>{x}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
              {!entradaRow.entradas_verificadas && !entradaLoading ? (
                <button type="button" style={btnMini} onClick={() => aprobarEntradaManual(entradaRow)}>
                  Aprobar manual
                </button>
              ) : null}
              <BtnGhost type="button" style={{ fontSize: 11 }} onClick={cerrarEntradaModal}>
                Cerrar
              </BtnGhost>
            </div>
          </div>
        </>
      ) : null}

      <AdminComunidadDuplicateCompareModal
        open={!!dupModalRow}
        sourceRow={dupModalRow}
        supabase={supabase}
        onClose={() => setDupModalRow(null)}
        onDismiss={dc => dupModalRow && patchRowDuplicateCheck(dupModalRow.id, dc)}
        onRefresh={dc => dupModalRow && patchRowDuplicateCheck(dupModalRow.id, dc)}
        onError={onError}
        onSuccess={onSuccess}
      />

      <AdminSocialKitModal
        open={!!socialKitOpen}
        onClose={() => setSocialKitOpen(null)}
        contentType="comunidad"
        contentId={socialKitOpen?.contentId ?? ''}
        titleHint={socialKitOpen?.titleHint ?? ''}
        onError={onError}
      />

      <div style={{ marginTop: 12, fontSize: 10, color: 'rgba(255,255,255,0.32)', lineHeight: 1.45 }}>
        En eventos con enlace de entradas, el botón «Comprar entradas» en la app solo aparece tras{' '}
        <strong style={{ color: 'rgba(255,255,255,0.5)' }}>verificación automática</strong> (al publicar o con «Verificar entradas») o
        aprobación manual.
      </div>
    </div>
  );
}
